import React from 'react';

/**
 * A team's avatar: its uploaded logo, or its colour with an initial.
 *
 * Mirrors the `TeamAvatar` inside Teams.js exactly — same radius ratio, same
 * gradient, same fallback — so a team looks like itself wherever it appears.
 * A team that had a picture on the Teams page and an invented initials tile on
 * the organisation page would read as two different teams.
 *
 * Kept here rather than exported from Teams.js because that file is ~1,700
 * lines of page and importing a page to draw an avatar is how a component
 * graph turns into a knot.
 */

// Same palette Teams derives from; the id keeps a team's colour stable for
// life, where a name-derived one would change on rename.
const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4'];

export function teamColor(team) {
  if (team?.color) return team.color;
  const s = String(team?.id ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % COLORS.length;
  return COLORS[h];
}

function darken(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
  if (!m) return hex;
  const shade = (v) => Math.max(0, Math.round(parseInt(v, 16) * 0.72));
  return `rgb(${shade(m[1])}, ${shade(m[2])}, ${shade(m[3])})`;
}

export default function TeamAvatar({ team, size = 32, className = '' }) {
  const radius = Math.round(size * 0.22);

  if (team?.logo_url) {
    return (
      <img
        className={className}
        src={team.logo_url}
        alt=""
        style={{ width: size, height: size, borderRadius: radius, flexShrink: 0, objectFit: 'cover' }}
      />
    );
  }

  const c = teamColor(team);
  return (
    <span
      className={className}
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexShrink: 0,
        background: `linear-gradient(135deg, ${c} 0%, ${darken(c)} 100%)`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.44),
        fontWeight: 700,
        color: '#fff',
        userSelect: 'none',
      }}
    >
      {(team?.name || '?').charAt(0).toUpperCase()}
    </span>
  );
}
